import { extensionOf } from './artifact-file';
import { FILE_TYPE_KINDS, fileTypeKind, type FileTypeKind } from './artifact-image';
import type { WorkspaceFile } from './workspace-files';

// The Files browser's pure core: folders derived from a file's path, and the date/type/creator/owner filters
// over a file list. No React, no data layer — every rule here is a plain function so it can be unit-tested
// without rendering, and so the SAME rule is mirrored 1:1 in iOS `FileBrowser.swift` and Android
// `FileBrowser.kt`.
//
// ## There is one path space, and it is the file-system's own key
//
// The file-system stores a workspace file under `workspaces/<workspaceId>/<path>`, and `<workspaceId>/<path>`
// is what `filePath` returns. That single decision is what lets one browser serve both Files surfaces: the
// cross-workspace page browses from the ROOT (so its first folder level is the workspace, resolved to a
// display name by the caller), and a workspace's own gallery is the same browser rooted at `<workspaceId>`.
// Neither has a tree of its own, so they cannot disagree about where a file lives.
//
// A consequence worth stating: folder navigation and the filters are independent. Filters narrow the whole
// SUBTREE under the current folder — filtering at the root searches everything — because a filter the user
// set is a question about their files, not about one directory.

/** The skills the user built with Maestro, in the file-system's own top-level `SKILLS` directory. */
export const SKILLS_ROOT = 'skills';

/** The `BRAIN` directory: an agent's own folder (`brain/<agentName>/`, keyed on the display name and moved
 *  when the agent is renamed) alongside shared topic areas like `wix-mcp` that belong to no agent. Drawn as
 *  "agents" — see `folderTitle`. */
export const BRAIN_ROOT = 'brain';

/** The `KNOWLEDGE` directory: what the agent REMEMBERS about the user — `self/`, `business/`, `feedback/`,
 *  `calendar/`, each a folder of notes the agent wrote for itself. Drawn as "Memory" (see `folderTitle`),
 *  because that is the question a reader brings to it: what does Symphony know about me, and can I make it
 *  forget. It is the ONE root whose rows carry a Delete — see `isMemoryPath` and `buildFileActions`.
 *
 *  `KNOWLEDGE.md` at its root is the agent's pointer index into the directory, not a memory; the listing
 *  hides it (`isReservedKnowledgePath`) and the delete routes refuse it. */
export const KNOWLEDGE_ROOT = 'knowledge';

/** The root segments of the path space that are NOT a Space.
 *
 *  These are the account's own directories: the file-system keeps them beside `workspaces` because they
 *  belong to the account rather than to one Space. The browser addresses them as `<root>/<path>` so they sit
 *  in the same single path space as everything else, and this list is what tells the workspace rules to
 *  leave those segments alone — none is a workspace id, each keeps its folder level at the merged root
 *  instead of being skipped, and nothing can be moved into any of them.
 *
 *  A workspace can never collide with one: a Space's segment is its id, not a name anyone typed. */
export const ACCOUNT_ROOTS = [SKILLS_ROOT, BRAIN_ROOT, KNOWLEDGE_ROOT];

/** The file-system directories a merged-tree path can name for a WRITE — the answer `writeDirectoryOf`
 *  gives, and the type every write hook takes so the two cannot disagree. */
export type WriteDirectory = 'workspaces' | 'brain' | 'knowledge';

/** Is this first-level segment one of the account's own directories rather than a Space? */
export function isAccountRoot(segment: string): boolean {
  return ACCOUNT_ROOTS.includes(segment);
}

/** Is this merged-tree path a MEMORY — a file under the knowledge root? The gate on the one destructive
 *  action the explorer offers: a memory row gets Delete, nothing else does. `knowledge` alone is the
 *  folder, not a memory. Mirrors native `FileBrowser.isMemoryPath`. */
export function isMemoryPath(path: string): boolean {
  const segments = pathSegments(path);
  return segments.length > 1 && segments[0] === KNOWLEDGE_ROOT && !isReservedKnowledgePath(segments.slice(1).join('/'));
}

/**
 * The key a merged-tree path is WRITTEN at, inside the directory `writeDirectoryOf` names.
 *
 * The browser shows one path space, but the file-system has several, and only the `workspaces` one is
 * keyed the way the tree reads. An agent's note is stored under `brain` as `<agent>/<file>`; the listing
 * PREFIXES it with `brain/` so it has somewhere to sit in the merged tree. A write has to take that prefix
 * back off, and until it did, creating a note in an agent's folder stored it at `brain/Riley/note.md`
 * INSIDE the brain directory — which the next listing prefixed again and drew as `brain/brain/Riley`.
 *
 * Always paired with `writeDirectoryOf`: one says which directory, this says the key within it. A caller
 * that uses one without the other is the bug this exists to stop.
 */
export function writeKeyOf(path: string): string {
  const segments = pathSegments(path);
  return isAccountRoot(segments[0] ?? '') ? segments.slice(1).join('/') : path;
}

/** Which file-system directory a path in the merged tree is WRITTEN to — the axis the browser itself
 *  does not have, because it browses one path space while a write names one directory.
 *
 *  It is derived from the path rather than passed down per surface, and that is the point: now that one
 *  Files screen shows Spaces, agents and skills together, "which directory does Save go to" is a
 *  question about the FILE under the cursor, not about the screen. A per-screen answer was right only
 *  while each tree had a screen of its own — and would silently write an agent's note into `workspaces`
 *  the moment the two lists merged.
 *
 *  `skills` is deliberately absent: `WRITABLE_DIRECTORIES` refuses it, so a skill has no directory to
 *  save into and the edit affordance is withheld rather than offered and then rejected by the server.
 *
 *  `knowledge` names its directory so that a write lands where the memory lives, but note that the write
 *  routes' allowlist does not admit it today: memory is changed by talking to the agent, and the only
 *  write the product offers on it from a Files screen is Delete (`/api/delete-file`, which takes the
 *  merged-tree path and does its own directory resolution). */
export function writeDirectoryOf(path: string): WriteDirectory | null {
  const root = pathSegments(path)[0] ?? '';
  if (root === BRAIN_ROOT) return 'brain';
  // Memory is not a place to write: no pencil, no `+`, no upload — like skills. The one write the
  // product offers on a memory row is Delete, which resolves its own directory. Mirrors both phones.
  if (root === KNOWLEDGE_ROOT) return null;
  if (root === SKILLS_ROOT) return null;
  return 'workspaces';
}

/** The folder holding ONE agent's own notes.
 *
 *  Keyed on the agent's DISPLAY NAME, not its id — that is the harness's choice (`brain/<agentName>/`), and
 *  a rename carries the folder across with `moveAgentBrainFolder`. So this resolves against the name the
 *  screen is already showing, and an agent renamed while the page is open simply points at the new folder.
 *  Mirrors native `FileBrowser.agentBrainFolder`. */
export function agentBrainFolder(agentName: string): string {
  return joinPath(BRAIN_ROOT, agentName);
}

/** Prefixes inside the SKILLS directory that hold Symphony's OWN machinery rather than a user's skill:
 *  `.learned/` the genome amendments, `.view/` the consolidated standing view, `.skill-learned/` the
 *  built-in-skill learning documents.
 *
 *  Every consumer filters these itself — the harness's `RESERVED_SKILL_PREFIXES` is the authority and says
 *  so, and symphony-bff's `MyAgents.java` already keeps its own copy. A prefix added there and not here
 *  puts raw bookkeeping JSON in the user's Files browser. Mirrored in iOS/Android `FileBrowser`. */
export const RESERVED_SKILL_PREFIXES = ['.learned/', '.view/', '.skill-learned/'];

/** Is this key inside the SKILLS directory one of Symphony's own documents? Takes the key AS THE DIRECTORY
 *  STORES IT (`.view/standing.json`), not the browser path, so it can be applied before anything enters the
 *  path space at all. */
export function isReservedSkillPath(path: string): boolean {
  return RESERVED_SKILL_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/** Symphony's own documents in the BRAIN directory — bookkeeping rather than anything an agent wrote for
 *  itself. `_genome-answered-items.json` is `GENOME_ANSWERED_FILE` in common-lib. Whole names, not prefixes:
 *  brain's machinery sits at the directory root while an agent's notes sit in a folder. */
export const RESERVED_BRAIN_PATHS = ['_genome-answered-items.json'];

/** Is this key inside the BRAIN directory one of Symphony's own documents? Same contract as
 *  `isReservedSkillPath` — the key as the directory stores it. */
export function isReservedBrainPath(path: string): boolean {
  return RESERVED_BRAIN_PATHS.includes(path);
}

/** The agent's own document in the KNOWLEDGE directory: `KNOWLEDGE.md`, its pointer index into what it
 *  remembers (what is where, and when to read it). Not a memory, so not a row — and the delete routes
 *  refuse it independently (`planMemoryDelete` in common-lib), because hiding a row is not a guard. A
 *  whole name at the directory root, like brain's: a note somebody filed as `self/KNOWLEDGE.md` is theirs. */
export const RESERVED_KNOWLEDGE_PATHS = ['KNOWLEDGE.md'];

/** Is this key inside the KNOWLEDGE directory the agent's index rather than a memory? Same contract as
 *  `isReservedBrainPath` — the key as the directory stores it. */
export function isReservedKnowledgePath(path: string): boolean {
  // The root index AND every Space's own index (`<space>/KNOWLEDGE.md`): the harness keeps one per
  // Space, and none of them is a memory.
  const last = pathSegments(path).slice(-1)[0] ?? '';
  return RESERVED_KNOWLEDGE_PATHS.includes(last);
}

/** A file as the browser addresses it: any `WorkspaceFile` plus the workspace it belongs to.
 *
 *  `name` on the wire is already the file's path WITHIN its workspace (`artifacts/report.pdf` — see
 *  `pages/api/workspace-files.ts`), which is why the row's displayed name has to be its last segment and
 *  cannot be `name` itself. */
export interface BrowsableFile extends WorkspaceFile {
  workspaceId?: string;
}

/** The file's full key in the one path space: `<workspaceId>/<name>`. A file with no workspace (a chat
 *  attachment) has no place in the tree and answers with just its name. */
export function filePath(item: BrowsableFile): string {
  return item.workspaceId ? `${item.workspaceId}/${item.name}` : item.name;
}

/** The folder part of a path: everything before the last `/`, with no trailing slash. `''` for a path with
 *  no folder at all (a file at the very root). */
export function folderOf(path: string): string {
  const slash = path.lastIndexOf('/');
  return slash < 0 ? '' : path.slice(0, slash);
}

/** The last segment of a path — the file's own name, or a folder's own name. */
export function nameOf(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** `folder` + `/` + `name`, skipping the separator at the root so a joined path never opens with `/`. */
export function joinPath(folder: string, name: string): string {
  return folder ? `${folder}/${name}` : name;
}

/** The path's segments, dropping empties so a stray double slash can't produce a nameless folder. */
export function pathSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/** Is `path` anywhere inside `folder` (at any depth)? Everything is inside the root. */
export function isInsideFolder(folder: string, path: string): boolean {
  return folder === '' || path.startsWith(`${folder}/`);
}

/** Is `path` a DIRECT child of `folder` — inside it with no further folder in between? */
export function isDirectChild(folder: string, path: string): boolean {
  return isInsideFolder(folder, path) && folderOf(path) === folder;
}

/** The immediate subfolder of `folder` that `path` sits under, or null when `path` is a direct child of
 *  `folder` (or outside it entirely). Returns the FULL path of that subfolder, not just its name — a folder
 *  is addressed by its path everywhere else, so returning a bare name would need re-joining at every call
 *  site. */
export function subfolderOf(folder: string, path: string): string | null {
  if (!isInsideFolder(folder, path) || isDirectChild(folder, path)) return null;
  const rest = folder === '' ? path : path.slice(folder.length + 1);
  const slash = rest.indexOf('/');
  if (slash < 0) return null;
  return joinPath(folder, rest.slice(0, slash));
}

// ── Filters ───────────────────────────────────────────────────────────────────────────────────────────────

/** The date buckets the Files filter offers, measured on the file's last-written date (falling back to its
 *  created date). Coarse on purpose: a date PICKER to answer "what did my agents make this week" is more
 *  work than the question deserves. */
export const FILE_DATE_FILTERS = ['any', 'today', 'last7Days', 'last30Days', 'older'] as const;
export type FileDateFilter = (typeof FILE_DATE_FILTERS)[number];

/** One writer, as the created-by filter identifies it: the actor kind plus the label it wrote under. The
 *  PAIR is the identity, so two sub-agents with different names are two options while an unnamed `SYSTEM`
 *  writer stays one.
 *
 *  The option list is DERIVED FROM THE FILES, never hardcoded — a fixed roster of agents would go stale the
 *  moment a new actor type writes anything, and would offer options that match nothing. */
export interface FileCreator {
  type: string;
  name: string;
}

/** What the user has narrowed the list to. All four dimensions are independent and AND together.
 *
 *  There used to be another, `owner` (anyone / your agents / you). It is gone because **created-by
 *  supersedes it**: picking "You" in Created by is the same question, asked once, against the same data — and
 *  a screen that offers both invites the state where they contradict each other.
 *
 *  `workspaceId` is the Space axis Tasks and Routines already have, and it is the one filter that also moves
 *  you: see `browseRoot`. */
export interface FileFilterState {
  /** null == every Space. The cross-workspace list is FLAT until this is set — see `browseRoot`. */
  workspaceId: string | null;
  /**
   * A SUBTREE this list is scoped to — an agent's own folder (`brain/<name>`) today.
   *
   * It is a filter and not a root, and that distinction is the whole point. A root is invisible and
   * inescapable: everything above it is sliced off the breadcrumb, so there is no path to read and no way
   * to climb out. A scope is a CHIP — you can see what is narrowing the list, and you can dismiss it to
   * get the whole tree back, in place. Same browser, same breadcrumb, one dismissible question.
   *
   * Like `workspaceId` it MOVES you rather than narrowing what a folder contains, so it becomes the
   * browsing root via `browseRoot` and is deliberately absent from `narrowsFiles`.
   */
  folder: string | null;
  /** The one the list is most often narrowed by, hence first among the question filters. */
  creator: FileCreator | null;
  date: FileDateFilter;
  /** null == every type. A concrete kind matches the file-type icon the row already shows, so the filter and
   *  the row agree by construction. */
  type: FileTypeKind | null;
}

export const NO_FILE_FILTERS: FileFilterState = { workspaceId: null, folder: null, creator: null, date: 'any', type: null };

/** Is anything actually narrowed? Drives the "Clear" affordance. */
export function hasActiveFilters(filters: FileFilterState): boolean {
  return (
    filters.workspaceId !== null ||
    filters.folder !== null ||
    filters.creator !== null ||
    filters.date !== 'any' ||
    filters.type !== null
  );
}

/** Does the current question FLATTEN the list — every match beneath the current folder, in one flat run,
 *  instead of this folder's directory listing?
 *
 *  The Space axis is deliberately NOT part of this. Picking a Space does not ask a question ABOUT the files in
 *  a folder, it says which folder you are in: it becomes the browsing root (`browseRoot`), so folders come back
 *  rather than going away. Every other axis, and the search box, narrows the files themselves and so flattens.
 *
 *  Keeping folder rows while narrowed would answer a question nobody asked: a file matches your filter one
 *  level down, so the screen says there is nothing here while the folder row beside it silently holds the
 *  match. */
export function narrowsFiles(filters: FileFilterState, query = ''): boolean {
  return filters.creator !== null || filters.date !== 'any' || filters.type !== null || query.trim() !== '';
}

/** Where the browser is actually rooted, given the surface's own root and the Space picked.
 *
 *  A workspace's own gallery has a real root and ignores the Space axis (there is only one Space to be in). On
 *  the cross-workspace surface the picked Space becomes the root, which is the same listing the merged root
 *  shows narrowed to that one Space — see `skipsWorkspaceLevel`. */
export function browseRoot(root: string, filters: FileFilterState): string {
  return root || filters.folder || filters.workspaceId || '';
}

/** Is this the cross-workspace surface's own root — the one place where the WORKSPACE level is skipped?
 *
 *  A workspace is not a folder anyone put a file in; it is the account's own partition, and it is already on the
 *  filter row as the Space chip. Drawing it as the first folder level meant every visit began by opening one
 *  before seeing a single file. So at this one folder — and nowhere else — the listing is every workspace's own
 *  contents merged: their folders, and any file sitting loose in one.
 *
 *  Only the top level is merged. Everything below it is an ordinary directory, because those folders ARE where
 *  someone filed something. */
export function skipsWorkspaceLevel(folder: string, root: string): boolean {
  return root === '' && folder === '';
}

/** The workspace roots present in `items` — the level the merged root skips over. An account root is not one
 *  of them: it is a directory nobody filed anything in by accident, so it stays a folder to open. */
export function workspaceRootsOf(items: BrowsableFile[]): string[] {
  return [...new Set(items.map((item) => pathSegments(filePath(item))[0]).filter((root) => root && !isAccountRoot(root)))];
}

/** A Space the list can be narrowed to: its id, and the name to draw. */
export interface FileWorkspaceOption {
  id: string;
  name: string;
}

/** The Spaces worth offering: those that actually own a file here, alphabetical by the name shown. Derived from
 *  the files, like every other option list on this row, so a menu can never offer a value that matches nothing.
 *  A Space whose name the caller cannot resolve is skipped rather than shown by id. */
export function workspacesOf(items: BrowsableFile[], names: Record<string, string>): FileWorkspaceOption[] {
  const present = new Set(items.map((item) => item.workspaceId).filter((id): id is string => !!id));
  return [...present]
    .map((id) => ({ id, name: names[id] ?? '' }))
    .filter((option) => option.name !== '')
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

const DAY_MS = 86_400_000;

/** Does `date` fall in `bucket`, relative to `now`? An item with NO date matches only `any` — it is unknown,
 *  not old, and putting it in `older` would be a claim the data doesn't support. */
export function matchesDateFilter(bucket: FileDateFilter, date: string | undefined, now: number): boolean {
  if (bucket === 'any') return true;
  if (!date) return false;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return false;
  if (bucket === 'today') return new Date(t).toDateString() === new Date(now).toDateString();
  const days = (now - t) / DAY_MS;
  if (bucket === 'last7Days') return days < 7;
  if (bucket === 'last30Days') return days < 30;
  return days >= 30;
}

/** The file-type bucket for an item — the same resolution the row's icon uses, so a file can never be filed
 *  under one type and drawn as another. */
export function fileTypeOf(item: WorkspaceFile): FileTypeKind {
  return fileTypeKind(extensionOf(item));
}

/** The item's writer identity, or null when it has none recorded (a file written before the store kept one).
 *  Such a file matches only "All agents".
 *
 *  Every unnamed agent kind collapses to ONE identity, because `creatorLabel` draws them all as the user's
 *  assistant name: leaving them distinct would put two options reading "Aria" in the Created-by menu, each
 *  filtering to a subset of the files that agent wrote. The identity has to match what is displayed. */
export function creatorOf(item: WorkspaceFile): FileCreator | null {
  const type = item.builtBy?.agentId?.trim();
  if (!type) return null;
  const name = item.builtBy?.name?.trim() ?? '';
  // A USER write carries the CLIENT as its name — the serverless bridge writes `app`, this app writes
  // `web` — so the name is a surface, not a person, and every one of them is the same human. Collapsing
  // them is the same rule as the unnamed agents below: the identity has to match what is displayed, and
  // `creatorLabel` draws them all as "You". Left distinct, the Created-by menu offers "web" and "app" as
  // two people, which is what it did.
  if (type.toUpperCase() === 'USER') return { type, name: '' };
  if (!name && AGENT_WRITER_TYPES.includes(type.toUpperCase())) return { type: 'MAESTRO', name: '' };
  return { type, name };
}

/** Actor kinds that ARE the user's own agent, and so answer to its name when they wrote under none. A
 *  `USER`, `SYSTEM` or `MIGRATION` write is not the agent and keeps its own label. */
const AGENT_WRITER_TYPES = ['MAESTRO', 'SUB_AGENT'];

/** The label to show for a writer, in four tiers.
 *
 *  1. **Its own name**, when the writer wrote one.
 *  2. **An unnamed agent → the user's assistant name.** Covers `MAESTRO`, which is RENAMEABLE (persona /
 *     custom name) and so has no fixed label to translate, AND an ad-hoc `SUB_AGENT`, which the harness
 *     writes with an empty name whenever it is not running as a named team agent (`writerFor` in
 *     `orion-harness-poc/src/tools/file-attribution.ts`). Callers pass the live name from `useAgentName()`.
 *  3. **A localized label for the actor kind** — `files.writer.*`.
 *  4. **The kind, humanised** (`SUB_AGENT` → "Sub agent") for an actor type that has no label yet. Never the
 *     raw wire enum: `MAESTRO` in the column read as shouting, which is the defect this tier fixes.
 *
 *  This whole function exists because "Built by" read `writer.name` alone, which the harness usually leaves
 *  unset (it is optional — see the writer-metadata map), so the column drew a coloured agent dot beside an
 *  empty string. Mirrors native `FileCreator.title`. */
export function creatorLabel(creator: FileCreator, t: (key: string) => string, assistantName?: string): string {
  // The user is "You", never the client that happened to write the file. A USER write's `name` is `web`
  // or `app` (the bridge's `APP_WRITER`), so tier 1 would answer with a surface where the catalog already
  // has the right word — which is why `files.writer.user` shipped translated in 37 locales and was never
  // once reached.
  if (creator.type.toUpperCase() === 'USER') return t('files.writer.user');
  if (creator.name) return creator.name;
  if (AGENT_WRITER_TYPES.includes(creator.type.toUpperCase()) && assistantName) return assistantName;
  const key = `files.writer.${creator.type.toLowerCase()}`;
  const localized = t(key);
  if (localized !== key) return localized;
  return humaniseActorType(creator.type);
}

/** `SUB_AGENT` → "Sub agent". A last resort for an actor type the catalog has no label for — readable, and
 *  visibly not a translated string, so a missing key is obvious rather than shipped as shouting. */
function humaniseActorType(type: string): string {
  const words = type.replace(/_/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Two creators are the same option when BOTH halves match — see `FileCreator`. */
export function sameCreator(a: FileCreator | null, b: FileCreator | null): boolean {
  if (a === null || b === null) return a === b;
  return a.type === b.type && a.name === b.name;
}

/** The creator options to offer, distinct and alphabetical by display name. Derived from the files in scope,
 *  so the filter can never offer an option that matches nothing. */
export function creatorsOf(items: WorkspaceFile[]): FileCreator[] {
  const byKey = new Map<string, FileCreator>();
  for (const item of items) {
    const creator = creatorOf(item);
    if (creator) byKey.set(`${creator.type} ${creator.name}`, creator);
  }
  return [...byKey.values()].sort((a, b) => (a.name || a.type).localeCompare(b.name || b.type, undefined, { sensitivity: 'base' }));
}

/** The type options to offer, in the canonical (design) order rather than discovery order, so the row of
 *  chips doesn't reshuffle when the file list changes. */
export function typesOf(items: WorkspaceFile[]): FileTypeKind[] {
  const present = new Set(items.map(fileTypeOf));
  return FILE_TYPE_KINDS.filter((kind) => present.has(kind));
}

/** The files matching `filters`, order preserved. `now` is injected so the date buckets are testable without
 *  waiting for a clock. */
export function applyFileFilters<T extends BrowsableFile>(items: T[], filters: FileFilterState, now = Date.now()): T[] {
  return items.filter(
    (item) =>
      (filters.workspaceId === null || item.workspaceId === filters.workspaceId) &&
      (filters.creator === null || sameCreator(creatorOf(item), filters.creator)) &&
      matchesDateFilter(filters.date, item.updatedDate ?? item.createdDate, now) &&
      (filters.type === null || fileTypeOf(item) === filters.type),
  );
}

/** Narrow to a free-text query over the file NAME (trimmed, case-insensitive substring). Matches the
 *  displayed name — the last path segment — not the whole key, so typing `report` finds
 *  `artifacts/report.pdf` and typing `artifacts` does not pretend every file in it is a name match. An empty
 *  query returns everything unchanged. */
export function searchFiles<T extends BrowsableFile>(items: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) => nameOf(item.name).toLowerCase().includes(q));
}

// ── The browser ───────────────────────────────────────────────────────────────────────────────────────────

/** One folder row in the list: its full path plus what OPENING it shows — its direct children, split into
 *  subfolders and files.
 *
 *  Both counts are direct children, never the subtree: a subtree count is a number the screen cannot then
 *  show you, so a row reading "4 files" above a single visible file reads as missing files rather than as
 *  nested ones. The row promises exactly what the next tap delivers. */
export interface FileFolderRow {
  path: string;
  name: string;
  /** Files sitting DIRECTLY in this folder. */
  fileCount: number;
  /** Immediate subfolders of this folder. */
  folderCount: number;
}

/** The immediate subfolders of `folder`, alphabetical, each with the counts of what it directly holds. A
 *  folder with no files under it at all does not exist — there is no empty directory in this store, so
 *  nothing can produce one, and no row can therefore be 0 files AND 0 folders. */
export function foldersIn(items: BrowsableFile[], folder: string): FileFolderRow[] {
  const subfolders = new Set<string>();
  for (const item of items) {
    const sub = subfolderOf(folder, filePath(item));
    if (sub) subfolders.add(sub);
  }
  return [...subfolders]
    .map((path) => ({ path, name: nameOf(path), ...directCountsIn(items, path) }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** What `folder` directly holds: files sitting in it, and its immediate subfolders. */
function directCountsIn(items: BrowsableFile[], folder: string): { fileCount: number; folderCount: number } {
  let fileCount = 0;
  const subfolders = new Set<string>();
  for (const item of items) {
    const path = filePath(item);
    if (isDirectChild(folder, path)) {
      fileCount += 1;
      continue;
    }
    const sub = subfolderOf(folder, path);
    if (sub) subfolders.add(sub);
  }
  return { fileCount, folderCount: subfolders.size };
}

/** A folder row's count line: what it directly holds, folders first then files, omitting a zero half. Never
 *  empty — a folder exists only because a file sits somewhere beneath it, so at least one half is non-zero.
 *  Three render sites draw this string and must agree, so it lives here. Mirrors native
 *  `FileFolderRow.countLabel`. */
export function folderCountLabel(row: FileFolderRow, t: (key: string, params?: Record<string, unknown>) => string): string {
  const parts: string[] = [];
  if (row.folderCount > 0) {
    parts.push(row.folderCount === 1 ? t('files.folder.subfolderCountOne') : t('files.folder.subfolderCount', { count: row.folderCount }));
  }
  if (row.fileCount > 0) {
    parts.push(row.fileCount === 1 ? t('files.folder.countOne') : t('files.folder.count', { count: row.fileCount }));
  }
  return parts.join(', ');
}

/** The files sitting DIRECTLY in `folder` (not in its subfolders). Order is the caller's — the table sorts
 *  by its own chosen column. */
export function filesIn<T extends BrowsableFile>(items: T[], folder: string): T[] {
  return items.filter((item) => isDirectChild(folder, filePath(item)));
}

/** The filters to APPLY, given where the browser is rooted.
 *
 *  A workspace's own gallery ignores the Space axis (`browseRoot` says so — there is only one Space to be in),
 *  and the filter predicate has to agree with that. It did not: a stale `?space=<other>` carried into
 *  `/workspace/<slug>/files` left `base` at this workspace while the predicate rejected every file in it, so the
 *  gallery went empty with no visible cause — the chip that would explain it is not even rendered there, since
 *  one Space is nothing to choose between. */
export function scopedFilters(root: string, filters: FileFilterState): FileFilterState {
  return root === '' ? filters : { ...filters, workspaceId: null };
}

/** The folder that "up" means from `folder` — what a Back control should show, and what the merged root makes
 *  different from a plain parent.
 *
 *  Opening `w-1/images` from the merged root skips the workspace level on the way in, so walking back out has to
 *  skip it on the way out too: its visible parent is the merged root, not `w-1`. Otherwise Back lands on a level
 *  the screen deliberately does not draw, which is the one place a user can meet it by accident.
 *
 *  (`w-1` remains perfectly reachable — that is what the Space chip and the breadcrumb are for. This is only
 *  about not putting it in the way of someone leaving a folder.)
 *
 *  `null` when there is nowhere up to go: `folder` IS the root. */
export function visibleParent(folder: string, root: string): string | null {
  if (folder === root) return null;
  const parent = folderOf(folder);
  // Only the WORKSPACE level is invisible. An account root is drawn as a folder row at the merged root, so
  // walking back out of one of its subfolders lands on it, not past it — skipping a level the user can see
  // and did open would be the opposite of the rule above.
  if (root === '' && !isAccountRoot(parent) && pathSegments(folder).length === 2) return '';
  return parent;
}

/** The Space a folder row belongs to, for the merged root — where two Spaces can each have an "images" and the
 *  row's own name no longer says which one you are about to open. Empty everywhere else: inside a Space the
 *  breadcrumb already says where you are. */
export function folderWorkspaceName(path: string, workspaceNames: Record<string, string>): string {
  const segments = pathSegments(path);
  if (segments.length < 2) return '';
  return workspaceNames[segments[0]] ?? '';
}

/** What to DRAW for a folder path. Only the first level needs resolving, and it has two kinds: a Space,
 *  whose segment is an id nobody can read, and an account root, which is translated. Every level below is a
 *  folder a user or an agent named — data, shown as it was named.
 *
 *  The account roots are the folders Symphony created rather than someone filing something, which is why
 *  they are the ones that get translated: leaving them raw shows an English word to every locale.
 *
 *  They are TITLE-CASE — "Skills", "Agents", "Memory" — because they are proper names of Symphony's own
 *  three directories, not descriptions of a pile of files. They were lowercase, on the reasoning that they
 *  should read as siblings of the `artifacts` and `images` beside them; that reasoning is retired along with
 *  the interleaving it belonged to (see `foldersAt`, which now pins them above the Space folders). It also
 *  never held in the catalogs: `files.folder.knowledge` has always shipped as "Memory", so the run read
 *  "skills · agents · Memory" — one of the three capitalised and the other two not, in every locale at once.
 *  German is the clearest tell, where `fähigkeiten`/`agenten` were simply wrong: German capitalises nouns.
 *
 *  `brain` is drawn as "Agents" — it holds an agent's own folder per agent, which is what a reader is
 *  looking for in it. The shared topic areas beside those (`wix-mcp`, `contacts`) sit under that name too;
 *  the product's decision is that one honest row beats splitting the directory in the UI.
 *
 *  Four sites draw a folder title (the table, the grid, the breadcrumb, the move dialog) and must agree, so
 *  the rule lives here. Mirrors native `FileBrowser.folderTitle`. */
export function folderTitle(path: string, workspaceNames: Record<string, string>, t: (key: string) => string): string {
  const segments = pathSegments(path);
  const last = segments[segments.length - 1] ?? '';
  if (segments.length !== 1) return last;
  if (path === SKILLS_ROOT) return t('files.folder.skills');
  if (path === BRAIN_ROOT) return t('files.folder.agents');
  // "Memory", not "knowledge": the directory is named for what the agent does with it, the row for what
  // the reader is looking for. Same word on all three surfaces.
  if (path === KNOWLEDGE_ROOT) return t('files.folder.knowledge');
  return workspaceNames[last] ?? last;
}

/** The folder rows to draw at `folder`. Identical to `foldersIn` everywhere except the cross-workspace root,
 *  where the workspace level is skipped and the listing is every workspace's own folders merged into one.
 *
 *  ## Symphony's own three directories are PINNED to the top, in `ACCOUNT_ROOTS` order
 *
 *  They used to be sorted in with the Space folders — one alphabetical run, on the reasoning that a row is a
 *  row. Two things are wrong with that, and both are about a list whose other rows the reader owns:
 *
 *  - **Their position moved for reasons that had nothing to do with them.** `Memory` sat above `Marketing`
 *    and below `Media`, so renaming a Space, or an agent filing the account's first file anywhere, silently
 *    relocated the row a reader had learned the position of. There are exactly three of them and they are
 *    always the same three, so they can have a fixed home.
 *  - **They are not the same KIND of row.** A Space is somewhere you put things; `Skills`, `Agents` and
 *    `Memory` are Symphony's own account-level directories, two of which you cannot even write to
 *    (`writeDirectoryOf`). Interleaving them asserted an equivalence the rest of the screen then denies.
 *
 *  The order is `ACCOUNT_ROOTS`' own — Skills, Agents, Memory — not alphabetical, so it does not reshuffle
 *  per locale. The Space folders below them keep their single alphabetical run, unchanged. Mirrored 1:1 by
 *  iOS `FileBrowser.folders(at:root:in:)` and Android `FileBrowser.foldersAt`. */
export function foldersAt(items: BrowsableFile[], folder: string, root: string): FileFolderRow[] {
  if (!skipsWorkspaceLevel(folder, root)) return foldersIn(items, folder);
  const accountFolders = foldersIn(items, '')
    .filter((row) => isAccountRoot(row.path))
    .sort((a, b) => ACCOUNT_ROOTS.indexOf(a.path) - ACCOUNT_ROOTS.indexOf(b.path));
  const spaceFolders = workspaceRootsOf(items)
    .flatMap((workspace) => foldersIn(items, workspace))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return [...accountFolders, ...spaceFolders];
}

/** The files to draw at `folder`: the ones sitting directly in it, or — at the merged root — every file sitting
 *  loose in a workspace, which would otherwise be reachable from nowhere. */
export function filesAt<T extends BrowsableFile>(items: T[], folder: string, root: string): T[] {
  if (!skipsWorkspaceLevel(folder, root)) return filesIn(items, folder);
  return workspaceRootsOf(items).flatMap((workspace) => filesIn(items, workspace));
}

/** Everything anywhere beneath `folder` — what the filters and the search run against, and what a folder
 *  row's count reports. */
export function subtreeOf<T extends BrowsableFile>(items: T[], folder: string): T[] {
  return items.filter((item) => isInsideFolder(folder, filePath(item)));
}

/**
 * Every folder a file could be MOVED into: every workspace's root, plus every folder that exists in any of
 * them, minus the folder it is already in. Alphabetical by the path shown.
 *
 * It spans workspaces on purpose. The agents are the same team in every workspace, so a workspace organises
 * work rather than gating access — moving a file from one to another is the same act as moving it between two
 * folders, and the destination list would be lying if it hid them.
 */
export function moveDestinations(item: BrowsableFile, items: BrowsableFile[]): string[] {
  if (!item.workspaceId) return [];
  const folders = new Set<string>();
  for (const other of items) {
    // The workspace root counts as a destination in its own right, not only as the parent of a folder — a
    // file can sit directly in a workspace.
    const path = filePath(other);
    const root = pathSegments(path)[0];
    // An account root is a directory the agent writes, not a place to file something: the move is a
    // `workspaces` write and the server would refuse it, so it must not be offered.
    if (isAccountRoot(root)) continue;
    if (root) folders.add(root);
    const folder = folderOf(path);
    if (folder) folders.add(folder);
  }
  folders.delete(folderOf(filePath(item)));
  return [...folders].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/** Is `folder` inside `root` — i.e. may the browser stay in it? The cross-workspace screen's root is the EMPTY
 *  string, which every path is inside; a `startsWith(`${root}/`)` test alone answers false for all of them, and
 *  a shared `?folder=…` link then snapped back to the root on mount. */
export function folderWithinRoot(folder: string, root: string): boolean {
  return root === '' || folder === root || folder.startsWith(`${root}/`);
}
